<template>
  <div
    ref="chartDomRef"
    class="w-full h-full"
  ></div>
</template>

<script setup>
import Definition from '@client/config/definition';
import { activityDetailStore, xAxisKeyMap } from '@client/store/activityDetailStore';
import { CHART_TYPE_UP_DOWN_HISTOGRAM, CHART_TYPE_PLURALITY_RECT } from '@client/pages/activity-detail/config/chart.types';
import { DistanceChart } from './chart.js';
import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
import { useActiveAttributes } from '../useActiveAttributes';
import useDrawChart from '../useDrawChart';

import { useListenResize } from '@client/hooks/useBoardResizeEvent';
import { onBeforeUnmount } from 'vue';
import { initStore } from '@client/store/initStore';
import { lapListTypeIsLap } from '../../table/hooks/useLapSelectValue';
import { getIndexNew, preProcessActivityIndex } from '../../table/utils/getIndexNew/getIndexNew';
import { newListData } from '../../table/hooks/useLapData';
import { curActivityDetail, isInterval, topSummary } from '../../../hooks/useActivityDetail';

const vm = getCurrentInstance();
const t = vm.proxy.$root.$t;

const initStoreRef = initStore();
const THEME = initStoreRef.theme;

// const chartDataType = 'overview';
const xAxisKey = xAxisKeyMap['index-rect'];
const chartRef = ref(null);
const chartDomRef = ref(null);
const { tryToDrawChart } = useDrawChart({ isIndexRect: true, drawChart });
const { activeAttributes } = useActiveAttributes();
const { cancelListner } = useListenResize(() => {
  tryToDrawChart();
});
onBeforeUnmount(cancelListner);

const activityDetailStoreRef = activityDetailStore();

const dataList = computed(() => {
  if (!newListData.value.length) {
    return [];
  }
  const curSummary = curActivityDetail.value.summary;
  const isProgram = !!curSummary.programId;

  const newChartData = [];
  const sportName = topSummary.value.sportName;

  let x_activityIndex = 0;
  let data = JSON.parse(JSON.stringify(newListData.value));
  // 游泳趟视图带有父级数据，需要过滤掉
  if ([300, 301].includes(curSummary.sportType)) {
    data = data.filter((i) => !i.x_isParentData);
  }

  data.forEach((item, index) => {
    // 健身类型需要去除休息数据
    if (['Strength'].includes(sportName) && Definition.isRestMode(item.mode)) {
      return;
    }

    // 判断是否被选中
    if (activityDetailStoreRef.selectedData.length === 0) {
      item.dataHadSelect = true;
    } else {
      item.dataHadSelect = activityDetailStoreRef.selectedData.includes(index);
    }

    // 是否为附属柱子
    let isSub = false;

    const lastIndex = newChartData.length - 1;

    if (isProgram && lapListTypeIsLap.value) {
      if (
        newChartData[lastIndex]?.data?.[0]?.exerciseIndex === item.exerciseIndex &&
        newChartData[lastIndex].data[0].setIndex === item.setIndex
      ) {
        isSub = true;
      }
    }

    // 计算颜色的透明度
    if (item.dataHadSelect) {
      if (isSub) {
        item.colorOpacity = 0.5;
      } else {
        item.colorOpacity = 1;
      }
    } else {
      item.colorOpacity = 0.08;
    }

    // 处理圈号，用到 x 轴
    const flag = preProcessActivityIndex({
      data: item,
      isInterval: isInterval.value,
      isProgram,
      isLap: false,
      sportName,
      isRunTest: curSummary.isRunTest === 1
    });
    if (flag) {
      x_activityIndex++;
      item.x_activityIndex = x_activityIndex;
    }
    if (isSub) {
      newChartData[lastIndex].data.push(item);
    } else {
      newChartData.push({
        xGroupIndex: newChartData.length + 1,
        data: [item]
      });
    }
  });

  return newChartData;
});
watch(dataList, () => {
  nextTick(() => {
    tryToDrawChart();
  });
});

watch(activeAttributes, tryToDrawChart);

// 绘制图表
function drawChart() {
  chartRef.value?.remove();

  chartRef.value = new DistanceChart({
    el: chartDomRef.value,
    theme: THEME,
    recommandLabel: t('S4493'),
    margin: {
      top: 20,
      bottom: 22,
      left: 40,
      right: 0
    },
    // x轴使用的属性字段名
    xAxisKey,
    // x轴刻度显示的文本信息转换方法，不传则直接使用属性值
    xTickInfo: xTickInfo,
    showYLine: false,
    onRectClick: onRectClick
  });
  drawXScale();
  setAttributes();
}

function xTickInfo(value) {
  const sportMode = curActivityDetail.value.summary.sportMode;
  const programId = curActivityDetail.value.summary.programId;
  const sportType = curActivityDetail.value.summary.sportType;
  const length = dataList.value.length;
  const index = value - 1;
  if (!(index >= 0)) {
    return {};
  }

  const sportName = Definition.getSportNameByType(sportType);

  let numStr;
  numStr = getIndexNew({
    data: dataList.value[index].data[0],
    isProgram: !!programId,
    sportMode,
    sportName
  });

  // 调整显示（减少x轴刻度的显示）
  let xTickText = numStr;
  if (length <= 44) {
  } else if (length <= 88) {
    xTickText = index % 4 === 0 ? numStr : '';
  } else if (length <= 132) {
    xTickText = index % 7 === 0 ? numStr : '';
  } else {
    xTickText = index % 13 === 0 ? numStr : '';
  }

  // if (['Strength'].includes(sportName)) {
  //   const nameStr = that.parentVm.getActivityName({
  //     listData: dataList.value,
  //     data: dataList.value[index].data[0],
  //     index: 0,
  //     isProgram: !!programId,
  //     sportName,
  //     sportMode
  //   })
  //   numStr = `${numStr} ${that.$t(nameStr)}`
  // }

  return {
    xTickText,
    text: numStr,
    value
  };
}

// 需要显示的刻度信息
function drawXScale() {
  const newDataList = JSON.parse(JSON.stringify(dataList.value));
  const length = newDataList.length || 0;

  // x轴刻度
  let xTicks = [];
  if (length <= 44) {
    xTicks = newDataList.map((item, index) => {
      return item[xAxisKey];
    });
  } else if (length <= 88) {
    xTicks = newDataList.map((item, index) => {
      return index % 4 === 0 ? item[xAxisKey] : '';
    });
  } else if (length <= 132) {
    xTicks = newDataList.map((item, index) => {
      return index % 7 === 0 ? item[xAxisKey] : '';
    });
  } else {
    xTicks = newDataList.map((item, index) => {
      return index % 13 === 0 ? item[xAxisKey] : '';
    });
  }

  const max = newDataList.length > 0 ? newDataList[newDataList.length - 1][xAxisKey] + 1 : 0;

  // 注入数据
  chartRef.value.setData(newDataList);
  // 创建x轴比例尺
  chartRef.value.createXAxis([0, max]);
  // 绘制x轴
  chartRef.value.drawXScale(xTicks);
}

// 设置显示的属性
function setAttributes() {
  const attrs = activeAttributes.value.map((item) => {
    const moreInfo = {
      chartType: CHART_TYPE_PLURALITY_RECT
    };

    // 左右平衡的上下柱状图
    // if (['leftGroundBalance', 'groundBalance'].includes(item.valueKey)) {
    if (['L_R_Balance'].includes(item.chartName)) {
      moreInfo.chartType = CHART_TYPE_UP_DOWN_HISTOGRAM;
      // moreInfo.upColor = '#77a4fa';
      // moreInfo.downColor = '#77d8fa';
    }
    return {
      ...item,
      ...moreInfo
    };
  });

  chartRef.value.setShowingAttributes(attrs);
}

function onRectClick(data) {
  // TODO: todo_xjf lapIndex 改为 listRowIndex
  const index = data.lapIndex - 1;
  const newData = JSON.parse(JSON.stringify(activityDetailStoreRef.selectedData));
  const spliceIndex = newData.indexOf(index);
  if (spliceIndex >= 0) {
    newData.splice(spliceIndex, 1);
  } else {
    newData.push(data.listRowIndex);
  }
  activityDetailStoreRef.updateSelectedData(newData);
}
</script>
